import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  ArrowLeft, User, Mail, CreditCard, Calendar, Shield, Trash2, 
  LogOut, ChevronRight, AlertTriangle, CheckCircle, Clock, Star
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { API } from '@/App';

export default function AccountSettingsPage() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    fetchUserData();
  }, []);

  const fetchUserData = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(response.data);
    } catch (error) {
      toast.error('Failed to load account information');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('display_name');
    localStorage.removeItem('user_id');
    toast.success('Logged out successfully');
    navigate('/');
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirmText !== 'DELETE') {
      toast.error('Please type DELETE to confirm');
      return;
    }

    setDeleting(true);
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/auth/delete-account`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      localStorage.removeItem('token');
      localStorage.removeItem('display_name');
      localStorage.removeItem('user_id');
      
      toast.success('Your account has been deleted');
      navigate('/');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete account');
    } finally {
      setDeleting(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getSubscriptionStatusBadge = (status) => {
    switch (status) {
      case 'active':
        return (
          <span className="inline-flex items-center gap-1 px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm font-medium">
            <CheckCircle className="w-4 h-4" /> Active
          </span>
        );
      case 'cancelled':
        return (
          <span className="inline-flex items-center gap-1 px-3 py-1 bg-amber-100 text-amber-800 rounded-full text-sm font-medium">
            <Clock className="w-4 h-4" /> Cancelled
          </span>
        );
      case 'expired':
        return (
          <span className="inline-flex items-center gap-1 px-3 py-1 bg-gray-100 text-gray-800 rounded-full text-sm font-medium">
            <AlertTriangle className="w-4 h-4" /> Expired
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center gap-1 px-3 py-1 bg-gray-100 text-gray-800 rounded-full text-sm font-medium">
            {status || 'Unknown'}
          </span>
        );
    }
  };

  const getPaymentSourceLabel = (source) => {
    switch (source) {
      case 'stripe': return 'Website (Stripe)';
      case 'apple': return 'Apple App Store';
      case 'google': return 'Google Play Store';
      case 'amazon': return 'Amazon Appstore';
      default: return source || 'N/A';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-primary-50 to-white flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-primary-50 to-white">
      <div className="max-w-2xl mx-auto px-4 py-8">
        <Button
          variant="ghost"
          onClick={() => navigate('/dashboard')}
          className="mb-6 text-secondary-700 hover:text-secondary-900 hover:bg-secondary-100"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Dashboard
        </Button>

        <h1 className="text-3xl font-serif font-bold text-gray-900 mb-8">Account Settings</h1>

        {/* Profile Section */}
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2 bg-primary/10 rounded-full">
              <User className="w-5 h-5 text-primary" />
            </div>
            <h2 className="text-xl font-semibold text-gray-900">Profile Information</h2>
          </div>

          <div className="space-y-4">
            <div className="flex items-center justify-between py-3 border-b border-gray-100">
              <div className="flex items-center gap-3">
                <User className="w-5 h-5 text-gray-400" />
                <span className="text-gray-600">Name</span>
              </div>
              <span className="text-gray-900 font-medium">{user?.display_name || 'N/A'}</span>
            </div>

            <div className="flex items-center justify-between py-3 border-b border-gray-100">
              <div className="flex items-center gap-3">
                <Mail className="w-5 h-5 text-gray-400" />
                <span className="text-gray-600">Email</span>
              </div>
              <span className="text-gray-900 font-medium">{user?.email || 'N/A'}</span>
            </div>

            <div className="flex items-center justify-between py-3">
              <div className="flex items-center gap-3">
                <Calendar className="w-5 h-5 text-gray-400" />
                <span className="text-gray-600">Member Since</span>
              </div>
              <span className="text-gray-900 font-medium">{formatDate(user?.created_at)}</span>
            </div>
          </div>
        </div>

        {/* Subscription Section */}
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2 bg-primary/10 rounded-full">
              <CreditCard className="w-5 h-5 text-primary" />
            </div>
            <h2 className="text-xl font-semibold text-gray-900">Subscription</h2>
          </div>

          <div className="space-y-4">
            <div className="flex items-center justify-between py-3 border-b border-gray-100">
              <span className="text-gray-600">Status</span>
              {getSubscriptionStatusBadge(user?.subscription_status)}
            </div>

            <div className="flex items-center justify-between py-3 border-b border-gray-100">
              <span className="text-gray-600">Plan</span>
              <span className="text-gray-900 font-medium">
                {user?.package_id === 'full_course' ? 'Full 12-Week Course' : 
                 user?.package_id === 'monthly' ? 'Monthly Plan' : 'N/A'}
              </span>
            </div>

            <div className="flex items-center justify-between py-3 border-b border-gray-100">
              <span className="text-gray-600">Purchased Via</span>
              <span className="text-gray-900 font-medium">
                {getPaymentSourceLabel(user?.subscription_source)}
              </span>
            </div>

            {user?.subscription_expires && (
              <div className="flex items-center justify-between py-3">
                <span className="text-gray-600">Access Until</span>
                <span className="text-gray-900 font-medium">{formatDate(user?.subscription_expires)}</span>
              </div>
            )}
          </div>

          <div className="mt-6 p-4 bg-gray-50 rounded-xl">
            <p className="text-sm text-gray-600">
              <strong>Need to manage your subscription?</strong><br />
              {user?.subscription_source === 'stripe' && 
                'Contact sundog.rose@gmail.com to cancel or modify your subscription.'}
              {user?.subscription_source === 'apple' && 
                'Go to Settings → Apple ID → Subscriptions on your iPhone to manage.'}
              {user?.subscription_source === 'google' && 
                'Go to Google Play → Subscriptions to manage.'}
              {user?.subscription_source === 'amazon' && 
                'Go to Amazon Appstore → Subscriptions to manage.'}
              {!user?.subscription_source && 
                'Contact sundog.rose@gmail.com for assistance.'}
            </p>
          </div>
        </div>

        {/* Admin Section - Only show for admins */}
        {user?.is_admin && (
          <div className="bg-white rounded-2xl shadow-lg p-6 mb-6 border-2 border-primary/20">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2 bg-primary/10 rounded-full">
                <Star className="w-5 h-5 text-primary" />
              </div>
              <h2 className="text-xl font-semibold text-gray-900">Admin</h2>
            </div>

            <div className="space-y-2">
              <button
                onClick={() => navigate('/admin/testimonials')}
                className="w-full flex items-center justify-between py-3 px-4 rounded-xl hover:bg-gray-50 transition-colors"
              >
                <span className="text-gray-700">Manage Testimonials</span>
                <ChevronRight className="w-5 h-5 text-gray-400" />
              </button>
            </div>
          </div>
        )}

        {/* Legal Links */}
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2 bg-primary/10 rounded-full">
              <Shield className="w-5 h-5 text-primary" />
            </div>
            <h2 className="text-xl font-semibold text-gray-900">Legal</h2>
          </div>

          <div className="space-y-2">
            <button
              onClick={() => navigate('/privacy')}
              className="w-full flex items-center justify-between py-3 px-4 rounded-xl hover:bg-gray-50 transition-colors"
            >
              <span className="text-gray-700">Privacy Policy</span>
              <ChevronRight className="w-5 h-5 text-gray-400" />
            </button>

            <button
              onClick={() => navigate('/terms')}
              className="w-full flex items-center justify-between py-3 px-4 rounded-xl hover:bg-gray-50 transition-colors"
            >
              <span className="text-gray-700">Terms of Service</span>
              <ChevronRight className="w-5 h-5 text-gray-400" />
            </button>
          </div>
        </div>

        {/* Logout */}
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
          <Button
            onClick={handleLogout}
            variant="outline"
            className="w-full py-6 text-gray-700 border-gray-300 hover:bg-gray-50"
          >
            <LogOut className="w-5 h-5 mr-2" />
            Sign Out
          </Button>
        </div>

        {/* Delete Account */}
        <div className="bg-white rounded-2xl shadow-lg p-6 border-2 border-red-100">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-red-100 rounded-full">
              <Trash2 className="w-5 h-5 text-red-600" />
            </div>
            <h2 className="text-xl font-semibold text-red-900">Delete Account</h2>
          </div>

          <p className="text-gray-600 mb-4">
            Permanently delete your account and all associated data. This action cannot be undone.
          </p>

          {!showDeleteConfirm ? (
            <Button
              onClick={() => setShowDeleteConfirm(true)}
              variant="outline"
              className="w-full py-4 text-red-600 border-red-300 hover:bg-red-50"
            >
              Delete My Account
            </Button>
          ) : (
            <div className="space-y-4">
              <div className="p-4 bg-red-50 rounded-xl">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-red-600 shrink-0 mt-0.5" />
                  <div className="text-sm text-red-800">
                    <p className="font-semibold mb-1">This will permanently delete:</p>
                    <ul className="list-disc ml-4 space-y-1">
                      <li>Your account and profile</li>
                      <li>All progress and completed tasks</li>
                      <li>Your photos and gallery</li>
                      <li>Community posts and comments</li>
                    </ul>
                  </div>
                </div>
              </div>

              <div>
                <Label htmlFor="deleteConfirm" className="text-gray-700">
                  Type <strong>DELETE</strong> to confirm
                </Label>
                <Input
                  id="deleteConfirm"
                  value={deleteConfirmText}
                  onChange={(e) => setDeleteConfirmText(e.target.value)}
                  placeholder="Type DELETE"
                  className="mt-2"
                />
              </div>

              <div className="flex gap-3">
                <Button
                  onClick={() => {
                    setShowDeleteConfirm(false);
                    setDeleteConfirmText('');
                  }}
                  variant="outline"
                  className="flex-1 py-4"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleDeleteAccount}
                  disabled={deleting || deleteConfirmText !== 'DELETE'}
                  className="flex-1 py-4 bg-red-600 hover:bg-red-700 text-white"
                >
                  {deleting ? 'Deleting...' : 'Permanently Delete'}
                </Button>
              </div>
            </div>
          )}
        </div>

        <div className="text-center mt-8 text-gray-500 text-sm">
          © 2026 Sundog Rosie. All rights reserved.
        </div>
      </div>
    </div>
  );
}
